/**
 * Pitch detector implementation using bluesharp-pitch-detection library
 * Wraps the YIN algorithm with our standard interface
 */

import type { PitchDetector, PitchDetectionResult } from "../types";
import { frequencyToNoteName, getCentsOffPitch } from "../noteUtils";

// Note: This will be imported once bluesharp-pitch-detection is installed
// For now, we'll define a minimal type to make TypeScript happy
type YINDetector = {
  detectPitch: (
    audioData: Float32Array,
    sampleRate: number
  ) => { pitch: number; confidence: number };
  setMinFrequency: (freq: number) => void;
  setMaxFrequency: (freq: number) => void;
  NO_DETECTED_PITCH: number;
};

/**
 * Adapter for bluesharp-pitch-detection's YIN algorithm
 */
export class BluesharpDetector implements PitchDetector {
  private detector: YINDetector | null = null;
  private minFrequency = 250; // Flute default
  private maxFrequency = 2200; // Flute default
  private confidenceThreshold = 0.8;

  constructor() {
    this.initializeDetector();
  }

  /**
   * Lazy-load the detector to avoid import errors if package not installed
   */
  private async initializeDetector() {
    try {
      // Dynamic import - will fail gracefully if package not installed
      const module = await import("bluesharp-pitch-detection");
      this.detector = module.YINPitchDetector as unknown as YINDetector;

      // Apply initial configuration
      this.detector.setMinFrequency(this.minFrequency);
      this.detector.setMaxFrequency(this.maxFrequency);

      console.log("[BluesharpDetector] Initialized with YIN algorithm");
    } catch (error) {
      console.error(
        "[BluesharpDetector] Failed to load bluesharp-pitch-detection:",
        error
      );
      console.warn(
        "[BluesharpDetector] Install with: npm install bluesharp-pitch-detection"
      );
    }
  }

  detectPitch(
    audioData: Float32Array,
    sampleRate: number
  ): PitchDetectionResult {
    if (!this.detector) {
      return {
        frequency: null,
        confidence: 0,
      };
    }

    const result = this.detector.detectPitch(audioData, sampleRate);

    // Check if pitch was detected
    if (result.pitch === this.detector.NO_DETECTED_PITCH) {
      return {
        frequency: null,
        confidence: 0,
      };
    }

    // Check confidence threshold
    if (result.confidence < this.confidenceThreshold) {
      return {
        frequency: null,
        confidence: result.confidence,
      };
    }

    // Valid detection - add note name and cents
    const frequency = result.pitch;
    const noteName = frequencyToNoteName(frequency);
    const centsOff = getCentsOffPitch(frequency);

    return {
      frequency,
      confidence: result.confidence,
      note: noteName,
      centsOff,
    };
  }

  setFrequencyRange(min: number, max: number): void {
    this.minFrequency = min;
    this.maxFrequency = max;

    if (this.detector) {
      this.detector.setMinFrequency(min);
      this.detector.setMaxFrequency(max);
    }
  }

  setConfidenceThreshold(threshold: number): void {
    if (threshold < 0 || threshold > 1) {
      throw new Error("Confidence threshold must be between 0 and 1");
    }
    this.confidenceThreshold = threshold;
  }

  getName(): string {
    return "Bluesharp YIN";
  }

  /**
   * Check if the detector is ready to use
   */
  isReady(): boolean {
    return this.detector !== null;
  }
}
